const aiConcepts = {
  title: "Core Concepts",
  subtitle: "The foundational ideas behind modern AI — what it actually is, how it learns, and why it works.",
  sections: [
    {
      id: "what-is-ai",
      title: "What is Artificial Intelligence?",
      content: [
        "Artificial Intelligence is the field of computer science focused on creating systems that can perform tasks that typically require human intelligence — recognizing patterns, understanding language, making decisions, and generating content [1].",
        "Modern AI is almost entirely built on machine learning: instead of writing explicit rules, you show the system examples and let it learn the patterns. This is a fundamental shift from traditional programming where every behaviour must be hand-coded [2].",
        "The AI you interact with today — ChatGPT, Copilot, image generators — are all products of deep learning, a subset of machine learning that uses neural networks with many layers to learn increasingly abstract representations of data."
      ]
    },
    {
      id: "neural-networks",
      title: "Neural Networks",
      content: [
        "A neural network is a mathematical function inspired loosely by biological neurons. It takes an input (text, image, numbers), passes it through layers of interconnected nodes, and produces an output (a prediction, classification, or generated content) [3].",
        "Each connection between nodes has a weight — a number that determines how much influence one node has on the next. Training a neural network means adjusting these weights so the network produces better outputs for a given set of inputs.",
        {
          type: "bullets",
          items: [
            "Input layer: receives raw data (pixels, words, numbers)",
            "Hidden layers: transform the data through learned representations — more layers means more abstract features",
            "Output layer: produces the final result (a probability, a word, a classification)",
            "Activation functions: introduce non-linearity, allowing networks to learn complex patterns beyond simple linear relationships"
          ]
        }
      ]
    },
    {
      id: "training",
      title: "How AI Models Learn",
      content: [
        "Training is the process of showing a model millions or billions of examples and adjusting its parameters (weights) to minimize errors. The key steps are forward pass (make a prediction), compute loss (measure how wrong it is), and backpropagation (adjust weights to reduce the error) [4].",
        "The quality and quantity of training data is often more important than the architecture of the model itself. A mediocre architecture trained on excellent data will usually outperform a brilliant architecture trained on poor data.",
        {
          type: "bullets",
          items: [
            "Supervised learning: learn from labelled examples (this image is a cat, this email is spam)",
            "Unsupervised learning: find patterns in unlabelled data (clustering, dimensionality reduction)",
            "Self-supervised learning: create labels from the data itself (predict the next word, fill in masked words) — this is how LLMs are trained",
            "Reinforcement learning: learn by trial and error with rewards and penalties (game playing, robotics, RLHF for LLMs)"
          ]
        }
      ]
    },
    {
      id: "transformers",
      title: "The Transformer Architecture",
      content: [
        "The Transformer is the architecture behind virtually all modern language models. Introduced in 2017 in the paper 'Attention Is All You Need', it replaced older sequence models (RNNs, LSTMs) by processing all tokens in parallel rather than sequentially [5].",
        "The key innovation is the attention mechanism — it allows the model to look at all parts of the input simultaneously and learn which parts are most relevant to each other. When processing 'The cat sat on the mat because it was tired', attention helps the model understand that 'it' refers to 'cat', not 'mat'.",
        {
          type: "bullets",
          items: [
            "Self-attention: each token attends to every other token, learning contextual relationships",
            "Multi-head attention: multiple attention patterns run in parallel, capturing different types of relationships",
            "Positional encoding: since transformers process all tokens at once, position information must be explicitly added",
            "Feed-forward layers: after attention, each position passes through the same neural network independently",
            "Layer normalization and residual connections: stabilize training and allow information to flow through deep networks"
          ]
        }
      ]
    },
    {
      id: "embeddings",
      title: "Embeddings and Vector Representations",
      content: [
        "An embedding is a way of representing words, sentences, or any data as a list of numbers (a vector) in a high-dimensional space. Similar things end up close together — 'king' and 'queen' are near each other, and far from 'bicycle' [6].",
        "Embeddings are the bridge between human-readable data and what neural networks actually process. Before a model can work with text, it converts each word or token into an embedding vector — typically hundreds or thousands of numbers.",
        "This is not just for text. Images, audio, code, and even user behaviour can be embedded into vector spaces, enabling similarity search, clustering, and retrieval across modalities."
      ]
    },
    {
      id: "inference",
      title: "Training vs Inference",
      content: [
        "Training is when the model learns — it is expensive, slow, and happens once (or periodically). Inference is when the trained model is used to make predictions — it is cheaper, faster, and happens every time you ask ChatGPT a question.",
        "The distinction matters practically: training GPT-4 cost over $100 million and took months. Running it to answer your question costs fractions of a cent and takes seconds. This asymmetry is why AI companies can offer powerful models at low per-query prices.",
        {
          type: "bullets",
          items: [
            "Training compute: measured in FLOPs (floating point operations) — modern LLMs require 10^24 to 10^25 FLOPs",
            "Inference compute: depends on model size and input length — optimized with techniques like quantization, distillation, and caching",
            "Fine-tuning: a middle ground — take a pre-trained model and train it further on a specific task with much less data and compute",
            "Context window: the amount of text a model can consider at once during inference — ranges from 4K to 1M+ tokens in modern models"
          ]
        }
      ]
    }
  ],
  references: [
    { id: 1, text: "Russell, S. & Norvig, P. — Artificial Intelligence: A Modern Approach (4th Edition, 2020)" },
    { id: 2, text: "LeCun, Y., Bengio, Y. & Hinton, G. — Deep Learning, Nature 521 (2015)" },
    { id: 3, text: "Goodfellow, I., Bengio, Y. & Courville, A. — Deep Learning (MIT Press, 2016)" },
    { id: 4, text: "Rumelhart, D., Hinton, G. & Williams, R. — Learning representations by back-propagating errors, Nature 323 (1986)" },
    { id: 5, text: "Vaswani, A. et al. — Attention Is All You Need, NeurIPS 2017", url: "https://arxiv.org/abs/1706.03762" },
    { id: 6, text: "Mikolov, T. et al. — Efficient Estimation of Word Representations in Vector Space (2013)", url: "https://arxiv.org/abs/1301.3781" }
  ]
};

export default aiConcepts;
